// src/legal/LegalAllInOnePage.jsx
import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

const TERMS_SECTIONS = [
  {
    id: "1",
    title: "Eligibility & Account Registration",
    content: [
      { label: "Eligibility", text: "You must be at least 18 and have legal capacity to enter into these Terms." },
      { label: "Accounts", text: "You are responsible for all activity under your account and safeguarding credentials." },
      { label: "Accurate Information", text: "You agree to provide accurate, current information and keep it updated." }
    ]
  },
  {
    id: "2",
    title: "Services & Conceptual Graphics",
    content: [
      { label: "Configuration & Quotes", text: "Digital configuration tools provide conceptual designs and quotes; separate workflows may allow you to request building plans." },
      { label: "Graphical Approximation", text: "On-screen visuals are conceptual and may include graphical errors, scaling discrepancies, omissions, or artifacts. You must not rely on screen graphics as final, field-ready, or code-compliant plans." },
      { label: "Professional Review Required", text: "Any plans or outputs require independent professional review, site-specific verification, and AHJ compliance checks by qualified design professionals." }
    ]
  },
  {
    id: "3",
    title: "Payments & Refunds",
    content: [
      { label: "Payments", text: "You authorize us/our processors to charge your payment method for all amounts due." },
      { label: "No Refunds", text: "All fees are non-refundable unless we expressly state otherwise in writing." },
      { label: "Chargeback Disputes", text: "If you initiate a chargeback, payment dispute, or reversal, you agree to pay liquidated damages equal to 200% of the disputed amount, plus our collection costs and reasonable attorneys' fees." },
      { label: "Taxes", text: "You are responsible for all applicable taxes and government charges." }
    ]
  },
  {
    id: "4",
    title: "Single-Use License",
    content: [
      { label: "Limited License", text: "Subject to full payment and compliance, we grant you a limited, non-exclusive, non-transferable, revocable license to use deliverables one time for a single specified street address you identify at order." },
      { label: "Restrictions", text: "No reuse, modification, adaptation, resale, sublicensing, sharing, derivative works, removal of notices, or use at any other address/project." },
      { label: "Records", text: "Maintain permanent records of the Licensed Site and provide them upon request." }
    ]
  },
  {
    id: "5",
    title: "Intellectual Property",
    content: [
      { label: "Ownership", text: "We own all right, title, and interest—including all copyrights, design rights, trade secrets, and other IP—in and to the Services, software, configurations, models, drawings, plans, templates, specs, documentation, outputs, derivatives, and improvements." },
      { label: "No Transfer", text: "Except for the limited license in Section 4, no rights are assigned or transferred." },
      { label: "Notices", text: "You must not remove or alter any proprietary notices on our IP." }
    ]
  },
  {
    id: "6",
    title: "User Content",
    content: [
      { label: "Your Rights", text: "You represent you have all rights to any content you upload." },
      { label: "License to Us", text: "You grant us a worldwide, non-exclusive, royalty-free license to host, store, reproduce, modify, translate, process, and display your content solely to provide/improve the Services." },
      { label: "No Sensitive Data", text: "Do not upload sensitive personal data unless we expressly request it in writing." }
    ]
  },
  {
    id: "7",
    title: "Professional Responsibility",
    content: [
      { label: "No Professional Services", text: "Using the Services does not create an engineering/architectural services relationship unless expressly agreed in a signed engagement." },
      { label: "Local Compliance", text: "You are responsible for all code compliance, site conditions, soils, structural requirements, zoning, permits, and inspections." },
      { label: "EOR/AOR", text: "Stamps or EOR/AOR services must be separately contracted and are not included unless explicitly stated in writing." }
    ]
  },
  {
    id: "8",
    title: "Technical Availability & Data",
    content: [
      { label: "Service Availability", text: "Services may be unavailable due to maintenance, updates, outages, or errors." },
      { label: "Data Loss", text: "Data may be lost, corrupted, or irretrievable. We do not guarantee backups and have no liability for data loss." },
      { label: "Your Responsibility", text: "You are solely responsible for exporting and safeguarding your copies." }
    ]
  },
  {
    id: "9",
    title: "Termination",
    content: [
      { label: "At-Will Termination", text: "We may suspend or terminate access at any time, with or without cause or notice." },
      { label: "Effect", text: "Upon termination, all rights granted to you cease. Provisions related to payments, IP, disclaimers, limitations, and indemnities survive." }
    ]
  },
  {
    id: "10",
    title: "Prohibited Conduct",
    content: [
      { label: "Restrictions", text: "You may not: (a) reverse engineer the Services; (b) build competing products; (c) introduce malware; (d) use outputs beyond the single-use license; (e) misrepresent site data or compliance; or (f) engage in unlawful activity." }
    ]
  },
  {
    id: "11",
    title: "Disclaimers",
    content: [
      { label: "As Is / As Available", text: "The Services and all outputs are provided 'AS IS' and 'AS AVAILABLE.'" },
      { label: "No Warranties", text: "To the maximum extent permitted by law, we disclaim all warranties, express or implied, including merchantability, fitness, accuracy, and non-infringement." },
      { label: "Graphics", text: "You acknowledge configurator visuals are approximate and may contain errors/omissions." }
    ]
  },
  {
    id: "12",
    title: "Limitation of Liability",
    content: [
      { label: "No Liability", text: "To the maximum extent permitted by law, we have no liability for any damages (including direct, indirect, consequential, lost profits, business interruption, construction costs, or permit failures) arising from the Services." },
      { label: "Cap", text: "If required by law, our aggregate liability is limited to the lesser of $100 or the amount you paid for the specific Service." }
    ]
  },
  {
    id: "13",
    title: "Indemnification",
    content: [
      { label: "Your Obligation", text: "You must defend, indemnify, and hold us harmless from all claims, losses, and costs (including attorneys' fees) arising from your use of the Services, your project, your content, or your breach of these Terms." },
      { label: "Fee-Shifting", text: "You agree to pay 100% of all costs and attorneys' fees we incur defending any claim you bring, regardless of outcome." }
    ]
  },
  {
    id: "14",
    title: "Dispute Resolution",
    content: [
      { label: "Governing Law", text: "Commonwealth of Virginia law applies." },
      { label: "Venue", text: "Claims must be brought in U.S. District Court for Eastern District of Virginia, state courts in Fairfax County, or arbitration in Fairfax County, at our election." },
      { label: "Waivers", text: "You agree to individual claims only (no class actions) and waive jury trial." },
      { label: "Time Limit", text: "Claims must be filed within one (1) year after accrual." }
    ]
  },
  {
    id: "15",
    title: "Compliance & Export",
    content: [
      { label: "Legal Compliance", text: "You must comply with all applicable laws." },
      { label: "Export Controls", text: "Do not export/re-export in violation of U.S. export control laws." },
      { label: "Third Parties", text: "We aren't responsible for third-party sites/services/content." }
    ]
  },
  {
    id: "16",
    title: "Copyright Enforcement",
    content: [
      { label: "Prohibition", text: "You may not remove, obscure, modify, or alter any copyright notice, watermark, logo, or proprietary mark without our written authorization." },
      { label: "Liquidated Damages", text: "Violations trigger liquidated damages of $25,000 per violation, plus all attorneys' fees, expert fees, court costs, and our staff time, whether willful or non-willful." }
    ]
  },
  {
    id: "17",
    title: "Changes & Notices",
    content: [
      { label: "Changes to Terms", text: "We may modify these Terms at any time. Material changes are effective when posted." },
      { label: "Electronic Notices", text: "You agree to receive notices electronically via email or through the Services." },
      { label: "Your Address", text: "You must keep your contact information current." }
    ]
  },
  {
    id: "18",
    title: "General Provisions",
    content: [
      { label: "Entire Agreement", text: "These Terms (plus any signed orders) constitute the entire agreement." },
      { label: "Severability", text: "If any provision is unenforceable, it will be modified to the minimum extent needed; the rest remains in effect." },
      { label: "Assignment", text: "You may not assign without our written consent; we may assign freely." },
      { label: "Acceptance", text: "By clicking 'I Agree,' creating an account, or using the Services, you affirmatively agree to these Terms." }
    ]
  }
];

const PRIVACY_SECTIONS = [
  {
    id: "1",
    title: "1. Information We Collect",
    content: [
      { label: "Account Information", text: "Name, email, phone, company, billing details, site address." },
      { label: "Project Data", text: "Site data, configuration parameters, uploaded files, notes." },
      { label: "Transaction Data", text: "Orders, invoices, payment confirmations." },
      { label: "Technical Data", text: "Device/browser type, IP address, cookies, logs, pages viewed." },
      { label: "Communications", text: "Support messages, feedback, and related metadata." }
    ]
  },
  {
    id: "2",
    title: "2. How We Use Information",
    content: [
      { label: "Service Delivery", text: "Provide, operate, secure, and improve the Services." },
      { label: "Outputs", text: "Generate configurations, quotes, and plans; maintain account records." },
      { label: "Payments", text: "Process payments and prevent fraud/abuse." },
      { label: "Communications", text: "Send updates, security notices, support, and marketing (where permitted)." },
      { label: "Legal Compliance", text: "Comply with law and enforce our rights." }
    ]
  },
  {
    id: "3",
    title: "3. Sharing & Disclosure",
    content: [
      { label: "Service Providers", text: "Hosting, analytics, support, payment processing vendors (bound by confidentiality)." },
      { label: "Legal Requirements", text: "To comply with law, legal process, or protect our rights and users." },
      { label: "Business Transfers", text: "In connection with mergers, acquisitions, or asset sales." }
    ]
  },
  {
    id: "4",
    title: "4. Cookies & Technologies",
    content: [
      { label: "Usage", text: "We use cookies for authentication, preferences, analytics, and security." },
      { label: "Control", text: "You may adjust browser settings to limit cookies; some features may not function properly." }
    ]
  },
  {
    id: "5",
    title: "5. Data Security & Retention",
    content: [
      { label: "Security", text: "We use reasonable technical and organizational safeguards. No system is 100% secure." },
      { label: "Retention", text: "We retain data as needed for the purposes in this Policy and to comply with legal obligations." }
    ]
  },
  {
    id: "6",
    title: "6. Your Rights & Choices",
    content: [
      { label: "Access", text: "You can access/update account information in your account settings." },
      { label: "Marketing", text: "Opt out of marketing emails via unsubscribe links (transactional notices still apply)." },
      { label: "International Users", text: "If accessing from outside the U.S., you consent to processing in the U.S." }
    ]
  },
  {
    id: "7",
    title: "7. Additional Provisions",
    content: [
      { label: "Children", text: "Services are not directed to children under 13. Do not use if under required age." },
      { label: "Third-Party Links", text: "We are not responsible for third-party sites/services accessed via links." },
      { label: "Policy Changes", text: "We may update this Policy. Material changes are effective when posted." }
    ]
  }
];

function TableOfContents({ sections, activeSection, onNavigate }) {
  return (
    <nav className="space-y-1">
      {sections.map((section) => (
        <button
          key={section.id}
          onClick={() => onNavigate(section.id)}
          className={`w-full text-left px-4 py-2 rounded-md text-sm transition ${
            activeSection === section.id
              ? 'bg-black text-white font-medium'
              : 'text-black/70 hover:bg-black/5'
          }`}
        >
          {/* <span className="text-black/40 font-mono text-xs mr-2">{section.id}</span> */}
          {section.title}
        </button>
      ))}
    </nav>
  );
}

function Section({ section }) {
  return (
    <section id={section.id} className="scroll-mt-24">
      <div className="border-l-2 border-black pl-6 mb-12">
        <div className="flex items-baseline gap-3 mb-6">
          {/* <span className="font-mono text-sm text-black/40">{section.id}</span> */}
          <h2 className="text-2xl font-bold text-black">{section.title}</h2>
        </div>
        
        <div className="space-y-6">
          {section.content.map((item, idx) => (
            <div key={idx} className="group">
              <h3 className="font-semibold text-black mb-2 flex items-center gap-2">
                <ChevronRight size={16} className="text-black/40" />
                {item.label}
              </h3>
              <p className="text-black/80 leading-relaxed ml-6">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function TermsPage() {
  const [activeTab, setActiveTab] = useState('terms');
  const [activeSection, setActiveSection] = useState('1');

  const currentSections = activeTab === 'terms' ? TERMS_SECTIONS : PRIVACY_SECTIONS;

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-black/10 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <Link to="/" className="text-sm text-black/60 hover:text-black transition">
              ← Back to Application
            </Link>
            <h1 className="text-xl font-bold text-black">My Designer Plus</h1>
            {/* <span className="text-xs text-black/40">Effective: October 14, 2025</span> */}
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1">
            <button
              onClick={() => {
                setActiveTab('terms');
                setActiveSection('1');
              }}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                activeTab === 'terms'
                  ? 'border-black text-black'
                  : 'border-transparent text-black/50 hover:text-black/70'
              }`}
            >
              Terms of Service
            </button>
            <button
              onClick={() => {
                setActiveTab('privacy');
                setActiveSection('p1');
              }}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                activeTab === 'privacy'
                  ? 'border-black text-black'
                  : 'border-transparent text-black/50 hover:text-black/70'
              }`}
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-12">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-32 h-fit">
            <div className="border border-black/10 rounded-md p-4">
              <h3 className="text-xs uppercase tracking-wider font-semibold text-black/50 mb-4">
                Contents
              </h3>
              <TableOfContents
                sections={currentSections}
                activeSection={activeSection}
                onNavigate={scrollToSection}
              />
            </div>
          </aside>

          {/* Main Content */}
          <main>
            {currentSections.map((section) => (
              <Section key={section.id} section={section} />
            ))}

            {/* Footer Info */}
            <div className="mt-16 pt-8 border-t border-black/10">
              <h3 className="font-semibold text-black mb-3">Contact Information</h3>
              <div className="text-sm text-black/70 space-y-1">
                <p className="font-medium text-black">My Designer Plus</p>
                <p>278 Cedar Lane #4017</p>
                <p>Vienna, VA 22180</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}